import { useState } from 'react';
import { ProductThumbnail, type ProductThumbnailDetails } from '../../shared/components/ProductThumbnail';

interface BarItem {
  label: string;
  value: number;
  image?: string;
  ean?: string;
  color: string;
  details?: ProductThumbnailDetails;
}

interface HorizontalBarChartProps {
  items: BarItem[];
  formatValue?: (v: number) => string;
}

export const HorizontalBarChart = ({ items, formatValue }: HorizontalBarChartProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const maxValue = Math.max(...items.map((i) => i.value), 1);

  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        const pct = (item.value / maxValue) * 100;
        const isHovered = hoveredIndex === i;
        const dimmed = hoveredIndex !== null && !isHovered;

        return (
          <div
            key={`${item.ean || item.label}-${i}`}
            className="group flex cursor-pointer items-center gap-3 transition-opacity duration-150"
            style={{ opacity: dimmed ? 0.45 : 1 }}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <ProductThumbnail src={item.image} alt={item.label} ean={item.ean} size="sm" details={item.details} hoverParent />
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center justify-between">
                <span className="min-w-0 flex-1 truncate pr-4 text-sm font-medium text-gray-700" title={item.label}>
                  {item.label}
                </span>
                <span className="flex-shrink-0 text-sm font-semibold text-gray-900 tabular-nums">
                  {formatValue ? formatValue(item.value) : item.value}
                </span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: item.color,
                    transform: isHovered ? 'scaleY(1.25)' : 'scaleY(1)',
                    transformOrigin: 'center',
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
